// @ts-nocheck
import { addItems, changeLine, clearCart, fetchCart } from './cartApi.js';
import { createEventBus } from './eventBus.js';
import { createRequestQueue } from './queue.js';
import { createRuleEngine, createDefaultRules } from './rules/index.js';
import { createCartStore } from './cartStore.js';
import { readCartSnapshot, saveCartSnapshot } from './utils/index.js';

export const CART_EVENTS = {
  READY: 'cart:ready',
  BEFORE_CHANGE: 'cart:before-change',
  AFTER_CHANGE: 'cart:after-change',
  UPDATED: 'cart:updated',
  ERROR: 'cart:error',
};

export class CartManager {
  constructor({ rules = createDefaultRules() } = {}) {
    this.events = createEventBus();
    this.queue = createRequestQueue();
    this.store = createCartStore(readCartSnapshot());
    this.rules = createRuleEngine(rules);
  }

  on(eventName, callback) {
    return this.events.on(eventName, callback);
  }

  off(eventName, callback) {
    this.events.off(eventName, callback);
  }

  getState() {
    return this.store.get();
  }

  registerRule(rule) {
    this.rules.register(rule);
  }

  setRuleEnabled(ruleName, enabled) {
    this.rules.setEnabled(ruleName, enabled);
  }

  async init() {
    const serverCart = await fetchCart();
    this.store.set(serverCart);
    saveCartSnapshot(serverCart);
    this.events.emit(CART_EVENTS.READY, {
      cart: serverCart,
      mutation: null,
      previousCart: null,
    });
    return serverCart;
  }

  async add(items) {
    return this.#executeMutation({ type: 'add', payload: { items } });
  }

  async update(lineKey, quantity) {
    return this.#executeMutation({
      type: 'update',
      payload: { lineKey, quantity },
    });
  }

  async remove(lineKey) {
    return this.#executeMutation({
      type: 'remove',
      payload: { lineKey },
    });
  }

  async clear() {
    return this.#executeMutation({ type: 'clear', payload: {} });
  }

  async refresh() {
    const cart = await fetchCart();
    this.store.set(cart);
    saveCartSnapshot(cart);
    return cart;
  }

  async #executeMutation(mutation) {
    const previousCart = this.store.get();
    this.events.emit(CART_EVENTS.BEFORE_CHANGE, {
      cart: previousCart,
      mutation,
      previousCart,
    });

    const validationResult = this.rules.validate(previousCart, mutation);

    if (!validationResult.allowed) {
      const errorPayload = {
        cart: previousCart,
        mutation,
        previousCart,
        error: validationResult.error || { code: 'RULE_REJECTED', message: 'Mutation rejected' },
      };
      this.events.emit(CART_EVENTS.ERROR, errorPayload);
      throw errorPayload.error;
    }

    const resolvedMutation = validationResult.modifiedMutation || mutation;

    try {
      const cart = await this.queue.enqueue(() => this.#dispatch(resolvedMutation));
      this.store.set(cart);
      saveCartSnapshot(cart);

      const payload = {
        cart,
        mutation: resolvedMutation,
        previousCart,
      };

      this.events.emit(CART_EVENTS.UPDATED, payload);
      this.events.emit(CART_EVENTS.AFTER_CHANGE, payload);

      return cart;
    } catch (error) {
      this.events.emit(CART_EVENTS.ERROR, {
        cart: previousCart,
        mutation: resolvedMutation,
        previousCart,
        error,
      });
      throw error;
    }
  }

  async #dispatch(mutation) {
    if (mutation.type === 'add') {
      await addItems(mutation.payload.items);
      return fetchCart();
    }

    if (mutation.type === 'update') {
      await changeLine({
        id: mutation.payload.lineKey,
        quantity: mutation.payload.quantity,
      });
      return fetchCart();
    }

    if (mutation.type === 'remove') {
      await changeLine({
        id: mutation.payload.lineKey,
        quantity: 0,
      });
      return fetchCart();
    }

    if (mutation.type === 'clear') {
      await clearCart();
      return fetchCart();
    }

    return fetchCart();
  }
}

let sharedCartManager = null;

export const createCartManager = (options) => {
  const hasCustomOptions = options && Object.keys(options).length > 0;
  if (hasCustomOptions) {
    return new CartManager(options);
  }

  if (!sharedCartManager) {
    sharedCartManager = new CartManager();
  }

  return sharedCartManager;
};
